import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import jsPDF from "jspdf";

export function TermsOfUse() {
  const auth = useSelector((x) => x.auth.value);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null); // To store user profile data
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = auth?.token;
        const response = await fetch(`${baseUrl}/currentuser`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setProfile(result.profile);
        setAccepted(result.profile.terms_and_conditions || false);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [auth?.token, baseUrl]);

  // Function to check which steps are complete based on profile fields
  const isStepComplete = () => {
    if (!profile) return false;

    // Check if Step 1 is complete
    if (!profile.geometryfile) {
      return "Please complete Step 1: Select a service area";
    }

    // Check if Step 2 is complete
    if (!profile.services) {
      return "Please complete Step 2: Select services provided";
    }

    // Check if Step 3 is complete
    if (!profile.description) {
      return "Please complete Step 3: Add a description";
    }

    // Check if Step 4 is complete (company and user details)
    const requiredFields = [
      "firstname",
      "lastname",
      "phonenumber",
      "company",
      "companywebsite",
      "address",
      "city",
      "state",
      "country",
      "ZIPCode",
      "companydetails",
    ];
    const incompleteFields = requiredFields.filter((field) => !profile[field]);

    if (incompleteFields.length > 0) {
      return "Please complete Step 4: Fill out all personal and company information";
    }

    // All steps are complete
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const incompleteStep = isStepComplete();
    if (incompleteStep) {
      alert(incompleteStep);
      return;
    }

    if (accepted) {
      setLoading(true);
      try {
        const token = auth?.token;
        const response = await fetch(`${baseUrl}/update_terms`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ terms_and_conditions: accepted }),
        });

        if (response.ok) {
          navigate("/home");
        } else {
          const result = await response.json();
          alert(result.message || "An error occurred.");
        }
      } catch (error) {
        console.error("Error:", error);
        alert("An error occurred.");
      } finally {
        setLoading(false);
      }
    } else {
      alert("Please accept the terms to proceed.");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height; // get page height
    let yPosition = 20; // initial Y position for text

    // Set document properties
    doc.setFontSize(12);
    doc.text("Terms of Service", 20, yPosition);
    yPosition += 10; // Update Y position after title
    doc.setFontSize(10);
    doc.text("Effective Date: October 23, 2024", 20, yPosition);
    yPosition += 10; // Update Y position after effective date

    // Add the terms text content
    let text = `1. Introduction

Welcome to GeoWGS84 Corp ("GeoWGS84," "we," "us," or "our"). These Terms of Service ("Terms") govern your use of our website, software, and other services (collectively, the "Services"). By accessing or using our Services, you agree to comply with and be bound by these Terms. If you do not agree to these Terms, you must not use our Services.

2. Use of Services

- Eligibility: To use our Services, you must be at least 18 years old and have the legal capacity to enter into binding agreements. By using our Services, you represent and warrant that you meet these requirements.

- Account Registration: To access certain features of our Services, you may be required to register an account. You agree to provide accurate and complete information during the registration process and to keep your account information up to date. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.

- Permitted Use: You agree to use our Services only for lawful purposes and in accordance with these Terms. You will not use our Services in any manner that could harm, disable, overburden, or impair our systems or interfere with other users' enjoyment of our Services.

3. Intellectual Property

- Ownership: All intellectual property rights in our Services, including but not limited to software, designs, trademarks, and content, are owned by GeoWGS84 or our licensors. You may not copy, modify, distribute, or create derivative works from our intellectual property without our prior written consent.

- License: Subject to your compliance with these Terms, GeoWGS84 grants you a limited, non-exclusive, non-transferable, revocable license to use our Services for your personal or internal business purposes.

4. Fees and Payment

- Fees: Fees for our Services are specified in the agreements or contracts you enter into with us. You agree to pay all fees as outlined and in accordance with the payment terms set forth.

- Payment Terms: Payments are due as specified in the applicable agreement or contract. Late payments may incur additional charges and interest.

5. Termination

- Termination by You: You may terminate your use of our Services at any time by notifying us in writing.  If a contract is in place you are obligated to the contract and cannot terminate it without an agreement with GeoWGS84.


- Termination by Us: We reserve the right to suspend or terminate your access to our Services, with or without cause, at our sole discretion. If your access is terminated due to a breach of these Terms, we may retain any fees paid.

6. Disclaimers and Limitation of Liability

- Disclaimer: Our Services are provided "as is" and "as available" without warranties of any kind, whether express or implied. GeoWGS84 does not warrant that our Services will be uninterrupted, error-free, or secure.

- Limitation of Liability: To the fullest extent permitted by law, GeoWGS84 shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with your use of our Services, including but not limited to loss of profits, data, or business opportunities.
All data acquired and processed will be the responsibility of GeoWGS84 business partners.  Our business partners are responsible for meeting the project specifications for data collection and processing and turnaround times.

7. Indemnification

You agree to indemnify, defend, and hold harmless GeoWGS84 and its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, or expenses (including reasonable attorneys' fees) arising out of or related to your use of our Services, your violation of these Terms, or any infringement of third-party rights.

8. Changes to Terms

We may revise these Terms from time to time. Any changes will be effective when we post the updated Terms on our website. Your continued use of our Services after such changes signifies your acceptance of the revised Terms.

9. Governing Law

These Terms will be governed by and construed in accordance with the laws of the State of Colorado, without regard to its conflict of law principles. Any disputes arising from these Terms will be subject to the exclusive jurisdiction of the courts located in Colorado.

10. Contact Us

For any questions or concerns about these Terms, please contact us at:

GeoWGS84 Corp  

GeoWGS84 Corp  
11973 S Longs Bluff Ln	
Parker, CO, 80134
(720) 702-4849
info@geowgs84.com


11. Miscellaneous

- Entire Agreement: These Terms constitute the entire agreement between you and GeoWGS84 regarding your use of our Services and supersede any prior agreements.

- Severability: If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.

- Waiver: Our failure to enforce any provision of these Terms does not constitute a waiver of our right to enforce such provision in the future.

Thank you for using GeoWGS84 Corp's Services!`;

    const lines = doc.splitTextToSize(text, 170); // 170 is the width

    // Loop through lines and add them to the PDF
    lines.forEach((line, index) => {
      // Check if the current Y position exceeds the page height
      if (yPosition + 10 > pageHeight) {
        doc.addPage(); // Add a new page if needed
        yPosition = 20; // Reset Y position for the new page
      }
      doc.text(line, 20, yPosition);
      yPosition += 10; // Update Y position for the next line
    });

    // Save the PDF
    doc.save("terms_of_service.pdf");
  };

  const incompleteStep = isStepComplete();
  return (
    <div className="terms-container">
      {loading ? (
        <div className="loading-spinner">
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color="#00BFFF"
            ariaLabel="three-dots-loading"
            visible={true}
          />
        </div>
      ) : (
        <>
          <h1 className="terms-heading">Terms of Service</h1>
          <p>Effective Date: October 23, 2024</p>
          <p>1. Introduction </p>
          <p>
            Welcome to GeoWGS84 Corp ("GeoWGS84," "we," "us," or "our"). These
            Terms of Service ("Terms") govern your use of our website, software,
            and other services (collectively, the "Services"). By accessing or
            using our Services, you agree to comply with and be bound by these
            Terms. If you do not agree to these Terms, you must not use our
            Services.
          </p>
          <p>2. Use of Services</p>
          <p>
            - Eligibility: To use our Services, you must be at least 18 years
            old and have the legal capacity to enter into binding agreements. By
            using our Services, you represent and warrant that you meet these
            requirements.
          </p>
          <p>
            - Account Registration: To access certain features of our Services,
            you may be required to register an account. You agree to provide
            accurate and complete information during the registration process
            and to keep your account information up to date. You are responsible
            for maintaining the confidentiality of your account credentials and
            for all activities that occur under your account.
          </p>
          <p>
            - Permitted Use: You agree to use our Services only for lawful
            purposes and in accordance with these Terms. You will not use our
            Services in any manner that could harm, disable, overburden, or
            impair our systems or interfere with other users' enjoyment of our
            Services.
          </p>
          <p>3. Intellectual Property </p>
          <p>
            - Ownership: All intellectual property rights in our Services,
            including but not limited to software, designs, trademarks, and
            content, are owned by GeoWGS84 or our licensors. You may not copy,
            modify, distribute, or create derivative works from our intellectual
            property without our prior written consent.
          </p>
          <p>
            - License: Subject to your compliance with these Terms, GeoWGS84
            grants you a limited, non-exclusive, non-transferable, revocable
            license to use our Services for your personal or internal business
            purposes.
          </p>
          <p>4. Fees and Payment</p>
          <p>
            - Fees: Fees for our Services are specified in the agreements or
            contracts you enter into with us. You agree to pay all fees as
            outlined and in accordance with the payment terms set forth.
          </p>
          <p>
            - Payment Terms: Payments are due as specified in the applicable
            agreement or contract. Late payments may incur additional charges
            and interest.
          </p>
          <p>5. Termination </p>
          <p>
            - Termination by You: You may terminate your use of our Services at
            any time by notifying us in writing. If a contract is in place you
            are obligated to the contract and cannot terminate it without an
            agreement with GeoWGS84.
          </p>
          <p>
            - Termination by Us: We reserve the right to suspend or terminate
            your access to our Services, with or without cause, at our sole
            discretion. If your access is terminated due to a breach of these
            Terms, we may retain any fees paid.
          </p>
          <p>6. Disclaimers and Limitation of Liability </p>
          <p>
            - Disclaimer: Our Services are provided "as is" and "as available"
            without warranties of any kind, whether express or implied. GeoWGS84
            does not warrant that our Services will be uninterrupted,
            error-free, or secure.
          </p>
          <p>
            - Limitation of Liability: To the fullest extent permitted by law,
            GeoWGS84 shall not be liable for any indirect, incidental, special,
            or consequential damages arising out of or in connection with your
            use of our Services, including but not limited to loss of profits,
            data, or business opportunities.
          </p>
          <p>
            {" "}
            All data acquired and processed will be the responsibility of
            GeoWGS84 business partners. Our business partners are responsible
            for meeting the project specifications for data collection and
            processing and turnaround times.
          </p>

          <p>7. Indemnification </p>
          <p>
            You agree to indemnify, defend, and hold harmless GeoWGS84 and its
            affiliates, officers, directors, employees, and agents from and
            against any claims, liabilities, damages, losses, or expenses
            (including reasonable attorneys' fees) arising out of or related to
            your use of our Services, your violation of these Terms, or any
            infringement of third-party rights.
          </p>
          <p>8. Changes to Terms</p>
          <p>
            We may revise these Terms from time to time. Any changes will be
            effective when we post the updated Terms on our website. Your
            continued use of our Services after such changes signifies your
            acceptance of the revised Terms.
          </p>
          <p>9. Governing Law</p>
          <p>
            These Terms will be governed by and construed in accordance with the
            laws of the State of Colorado, without regard to its conflict of law
            principles. Any disputes arising from these Terms will be subject to
            the exclusive jurisdiction of the courts located in Colorado.
          </p>
          <p>10. Contact Us </p>
          <p>
            For any questions or concerns about these Terms, please contact us
            at:
          </p>

          <p>
            GeoWGS84 Corp <br />
            11973 S Longs Bluff Ln <br />
            Parker, CO, 80134 <br />
            (720) 702-4849 <br />
            info@geowgs84.com{" "}
          </p>
          <p>11. Miscellaneous </p>
          <p>
            - Entire Agreement: These Terms constitute the entire agreement
            between you and GeoWGS84 regarding your use of our Services and
            supersede any prior agreements.
          </p>
          <p>
            - Severability: If any provision of these Terms is found to be
            invalid or unenforceable, the remaining provisions will continue in
            full force and effect.
          </p>
          <p>
            - Waiver: Our failure to enforce any provision of these Terms does
            not constitute a waiver of our right to enforce such provision in
            the future.
          </p>
          <p>Thank you for using GeoWGS84 Corp's Services! </p>
          <button onClick={generatePDF}>Download Terms and Conditions</button>
          <br />
          <br />
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="termsCheckbox" className="terms-checkbox">
              <Form.Check
                type="checkbox"
                label="I agree to the Terms and Conditions"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
            </Form.Group>

            <div className="button-container" style={{ paddingBottom: '50px'}}>
              <Button
                className="btn btn-primary"
                variant="primary"
                type="submit"
                disabled={!!incompleteStep || !accepted}
              >
                Finish Your Registration
              </Button>
            </div>

            {incompleteStep && (
              <p className="incomplete-step-message" style={{ color: "red" }}>
                {incompleteStep}
              </p>
            )}
          </Form>
        </>
      )}
    </div>
  );
}
